// Сервис для работы с push-уведомлениями
class PushNotificationService {
  private baseUrl: string;

  constructor() {
    try {
      this.baseUrl = process.env.REACT_APP_API_URL || (() => {
        if (typeof window === 'undefined' || typeof window.location === 'undefined') {
          return 'http://localhost:8000';
        }
        // Same-origin (relative) for production; localhost dev hits :8000 directly.
        return window.location.hostname === 'localhost' ? 'http://localhost:8000' : '';
      })();
    } catch (error) {
      console.warn('Error initializing PushNotificationService:', error);
      this.baseUrl = 'http://localhost:8000';
    }
  }

  // Проверка поддержки push-уведомлений
  isSupported(): boolean {
    try {
      // Проверяем, что мы в браузере
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
      }
      
      return 'serviceWorker' in navigator && 
             'PushManager' in window && 
             'Notification' in window;
    } catch (error) {
      console.warn('Error checking push notification support:', error);
      return false;
    }
  }

  // Запрос разрешения на уведомления
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    if (!('Notification' in window)) {
      throw new Error('Notification API not supported');
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw new Error('Failed to request notification permission');
    }
  }

  // Подписка на push-уведомления
  async subscribe(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    // Получаем VAPID ключ с сервера
    let vapidKey: string;
    
    try {
      const response = await fetch('/api/notifications/vapid-public-key');
      if (!response.ok) {
        throw new Error(`Failed to get VAPID key: ${response.status}`);
      }
      
      const data = await response.json();
      vapidKey = data.vapidPublicKey;
      
      if (!vapidKey) {
        throw new Error('VAPID key not received from server');
      }
      
      // Проверяем формат VAPID ключа
      if (vapidKey.length < 50) {
        console.warn('❌ VAPID key seems too short, length:', vapidKey.length);
        throw new Error('VAPID key format invalid');
      }
      
      // Проверяем, что ключ содержит только допустимые символы
      const trimmedKey = vapidKey.trim();
      if (!/^[A-Za-z0-9+/_-]+$/.test(trimmedKey)) {
        console.warn('❌ VAPID key contains invalid characters');
        console.warn('❌ Invalid characters found:', trimmedKey.split('').filter(char => !/^[A-Za-z0-9+/_-]$/.test(char)));
        throw new Error('VAPID key contains invalid characters');
      }
      
    } catch (error) {
      console.error('❌ Failed to get VAPID key from server:', error);
      throw new Error('Failed to get VAPID key from server');
    }

    try {
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker not supported');
      }

      if (!('PushManager' in window)) {
        throw new Error('Push Manager not supported');
      }

      // Wait for the SW (registered once in serviceWorkerRegistration.ts).
      // DO NOT call register() here — that caused duplicate registrations.
      const registration = await navigator.serviceWorker.ready;

      // Return existing subscription if any, otherwise fall through to create one.
      try {
        const existing = await registration.pushManager.getSubscription();
        if (existing) return existing;
      } catch (err) {
        console.error('[push] subscription check failed:', err);
      }
       
      // Create a new push subscription
      let applicationServerKey: Uint8Array;
      try {
        applicationServerKey = this.urlBase64ToUint8Array(vapidKey);
        if (applicationServerKey.length === 0) throw new Error('empty VAPID key');
      } catch (err) {
        console.error('[push] VAPID key conversion failed:', err);
        throw new Error('VAPID key conversion failed');
      }

      let subscription: PushSubscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      } catch (err) {
        console.error('[push] PushManager.subscribe failed:', err);
        throw err;
      }

      // Отправляем подписку на сервер
      await this.sendSubscriptionToServer(subscription);
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      
      // Дополнительная диагностика
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.error('AbortError - возможно, браузер заблокировал push-уведомления');
        } else if (error.name === 'InvalidAccessError') {
          console.error('InvalidAccessError - проблема с VAPID ключом или разрешениями');
        } else if (error.name === 'NotAllowedError') {
          console.error('NotAllowedError - пользователь не разрешил уведомления');
        }
      }
      
      return null;
    }
  }

  // Отписка от push-уведомлений
  async unsubscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      if (!('serviceWorker' in navigator)) {
        return false;
      }

      if (!('PushManager' in window)) {
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await this.removeSubscriptionFromServer(subscription);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }

  // Проверка статуса подписки
  async getSubscriptionStatus(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      if (!('serviceWorker' in navigator)) {
        return false;
      }

      if (!('PushManager' in window)) {
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  // Отправка подписки на сервер
  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    try {
      if (typeof fetch === 'undefined') {
        throw new Error('fetch not supported');
      }

      const token = localStorage.getItem('token');
      const userId = this.getUserId();
      
      const response = await fetch(`${this.baseUrl}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          userId: userId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Сервер вернул ошибку:', errorText);
        throw new Error(`Server error: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
    } catch (error) {
      console.error('❌ Ошибка отправки push-подписки на сервер:', error);
      throw error; // Пробрасываем ошибку дальше
    }
  }

  // Удаление подписки с сервера
  private async removeSubscriptionFromServer(subscription: PushSubscription): Promise<void> {
    try {
      if (typeof fetch === 'undefined') {
        throw new Error('fetch not supported');
      }

      const token = localStorage.getItem('token');
      await fetch(`${this.baseUrl}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          userId: this.getUserId()
        })
      });
    } catch (error) {
      console.error('Error removing subscription from server:', error);
    }
  }

  // Получение ID пользователя из токена
  private getUserId(): string | null {
    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return null;
      }

      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return payload.userId || payload.id;
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  }

  // Конвертация VAPID ключа
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    try {
      if (typeof window === 'undefined' || typeof window.atob === 'undefined') {
        throw new Error('atob not supported');
      }

      // Убираем возможные пробелы, переносы строк и другие символы
      const cleanKey = base64String.trim().replace(/[\s\n\r\t]/g, '');
      // Проверяем, что ключ содержит только допустимые символы
      if (!/^[A-Za-z0-9+/_-]+$/.test(cleanKey)) {
        console.error('🔍 Invalid characters found in VAPID key');
        console.error('🔍 Cleaned key:', cleanKey);
        console.error('🔍 Characters that failed validation:', cleanKey.split('').filter(char => !/^[A-Za-z0-9+/_-]$/.test(char)));
        throw new Error('VAPID key contains invalid characters');
      }
      
      // Правильная конвертация URL-safe base64 в стандартный base64
      let base64 = cleanKey;
      
      // Заменяем URL-safe символы на стандартные base64
      base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
      
      // Добавляем padding если нужно
      while (base64.length % 4) {
        base64 += '=';
      }
      
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);

      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      
      // Проверяем размер массива - VAPID ключ должен быть 65 байт для EC ключей
      if (outputArray.length !== 65) {
        console.warn('⚠️ VAPID key array length is not 65 bytes:', outputArray.length);
        console.warn('⚠️ This might cause issues with some browsers');
      }
      
      return outputArray;
    } catch (error) {
      console.error('❌ Error converting VAPID key:', error);
      console.error('❌ VAPID key value:', base64String);
      console.error('❌ VAPID key length:', base64String.length);
      console.error('❌ Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      throw new Error('Failed to convert VAPID key');
    }
  }
}

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
